/**
 * ProShip Direct v2 — pick couriers, then prioritise them.
 *
 * Two panes, because one list does not work at this scale. A merchant has 110+ ProShip courier
 * codes plus their own; ranking inside that list means dragging through a hundred rows to move
 * one courier, and it implies every courier needs a position. In practice a rule names three or
 * four.
 *
 * Choose on the left, order on the right. Both panes keep rows to a single line with the courier
 * code truncated — codes run long (Shadowfax_Reverse_Mile_Collective_PRZ_1) and wrapping them
 * doubled every row's height, which is what made the first version unreadable. Full value is in
 * each row's tooltip.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, InputAdornment, Button, Checkbox, Tooltip,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { secondaryBtnSx } from '../../../styles/patterns';
import { Link } from 'react-router-dom';
import { SUBTLE, NAVY, BORDER, WHITE, LIGHT_BG, WARNING, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { MONO } from './directCellStyles';
import { OwnerChip } from './chips';
import { statLabelSx } from './stats';
import PriorityList from './PriorityList';
import { SETTINGS_PATHS } from '../flags';

const subSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE };
const CONTROL_HEIGHT = 32;
const PANE_HEIGHT = 296;

const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: FONT, fontSize: SIZE['2xs'], height: CONTROL_HEIGHT },
};

const paneSx = {
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS.md,
  bgcolor: WHITE,
  display: 'flex',
  flexDirection: 'column',
  height: PANE_HEIGHT,
  minWidth: 0,
};

const paneHeadSx = {
  px: 1,
  py: 0.75,
  borderBottom: `1px solid ${BORDER}`,
  display: 'flex',
  alignItems: 'center',
  gap: GAP.sm,
  flexShrink: 0,
};

/** Title row inside a pane head that also carries controls. */
const paneTitleRowSx = { display: 'flex', alignItems: 'center', gap: GAP.sm, minWidth: 0 };

/**
 * Search and filter, side by side and filling the pane.
 *
 * nowrap on purpose: these two belong together, and at this pane width a wrapping row put them on
 * separate lines and ate a third of the list's height. The search flexes, the filter is fixed and
 * narrow, so the pair fits whatever the column does.
 */
const paneControlsRowSx = { display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', minWidth: 0 };

const rowSx = (checked) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 0.5,
  py: 0.35,
  borderRadius: RADIUS.sm,
  bgcolor: checked ? `${PRIMARY_BLUE}0A` : 'transparent',
  cursor: 'pointer',
  minWidth: 0,
  '&:hover': { bgcolor: checked ? `${PRIMARY_BLUE}14` : LIGHT_BG },
});

const nameSx = {
  fontFamily: FONT,
  fontSize: SIZE['2xs'],
  fontWeight: WEIGHT.semibold,
  color: NAVY,
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const codeSx = {
  fontFamily: MONO,
  fontSize: SIZE['4xs'],
  color: SUBTLE,
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const OWNER_FILTERS = [
  { value: '', label: 'All types' },
  { value: 'DIRECT', label: 'Direct' },
  { value: 'PROSHIP', label: 'ProShip' },
];

export default function CourierPriorityPicker({ pool, order, onChange, readOnly, withheld }) {
  const [query, setQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [checked, setChecked] = useState([]);

  /*
    DERIVED FROM THE POOL, not from the plan.

    "Direct or ProShip?" is only a question when the list holds both. Without the Direct plan the
    pool is ProShip couriers alone, and a three-option filter over one kind is a control that can
    only ever return everything or nothing.

    Reading it off the pool rather than plan.active also covers the case the plan flag misses: a
    merchant who has the plan but has not connected an account yet, where the filter is equally
    pointless. The pool is the thing the filter acts on, so it is the thing that should decide.
  */
  const owners = useMemo(() => new Set(pool.map((c) => c.owner)), [pool]);
  const showOwnerFilter = owners.size > 1;

  /* A hidden filter must not still be filtering — a stale DIRECT would empty the list silently. */
  const activeOwnerFilter = showOwnerFilter ? ownerFilter : '';

  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pool.filter((c) => {
      if (order.includes(c.key)) return false;
      if (activeOwnerFilter && c.owner !== activeOwnerFilter) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.detail.toLowerCase().includes(q);
    });
  }, [pool, order, query, activeOwnerFilter]);

  const addKeys = (keys) => {
    const fresh = keys.filter((k) => !order.includes(k));
    if (!fresh.length) return;
    onChange([...order, ...fresh]);
    setChecked([]);
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: GAP.md }}>
      {/* Left — choose */}
      <Box sx={paneSx}>
        <Box sx={{ ...paneHeadSx, flexDirection: 'column', alignItems: 'stretch', gap: 0.75 }}>
          <Box sx={paneTitleRowSx}>
            <Typography sx={statLabelSx}>Available</Typography>
            <Typography sx={subSx}>{available.length}</Typography>
            <Box sx={{ flex: 1, minWidth: 8 }} />
            {(query || activeOwnerFilter) && (
              <Typography
                onClick={() => {
                  setQuery('');
                  setOwnerFilter('');
                }}
                sx={{
                  ...subSx,
                  color: PRIMARY_BLUE,
                  cursor: 'pointer',
                  fontWeight: WEIGHT.semibold,
                  whiteSpace: 'nowrap',
                }}
              >
                Clear
              </Typography>
            )}
          </Box>

          <Box sx={paneControlsRowSx}>
            <TextField
              size='small'
              placeholder='Search couriers'
              value={query}
              disabled={readOnly}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchOutlinedIcon sx={{ fontSize: 14, color: SUBTLE }} />
                  </InputAdornment>
                ),
              }}
            />
            {/*
              No floating label: at this size the label needs its own line above the field, and
              "All types" as the resting value says the same thing in the space the control already
              occupies. The search box takes the freed width when this is not shown.
            */}
            {showOwnerFilter && (
              <TextField
                select
                size='small'
                value={ownerFilter}
                disabled={readOnly}
                onChange={(e) => setOwnerFilter(e.target.value)}
                sx={{ ...fieldSx, width: 104, flexShrink: 0 }}
              >
                {OWNER_FILTERS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </Box>

        {/*
          WHY A COURIER IS NOT IN THIS LIST.

          Only enabled couriers can be picked, which is correct and completely opaque: a merchant
          who has switched ProShip's couriers off sees their own three accounts and no sign that a
          hundred more exist one screen away. This names the omission and links to the screen that
          undoes it, rather than leaving them to work out that the picker is not broken.
        */}
        {withheld?.proship > 0 && (
          <Box
            sx={{
              px: 1,
              py: 0.6,
              borderBottom: `1px solid ${BORDER}`,
              bgcolor: `${WARNING}0f`,
              flexShrink: 0,
            }}
          >
            <Typography sx={subSx}>
              {withheld.proship} ProShip courier{withheld.proship === 1 ? ' is' : 's are'} switched
              off and cannot be picked.{' '}
              <Box
                component={Link}
                to={SETTINGS_PATHS.manageCouriers}
                sx={{ color: PRIMARY_BLUE, fontWeight: WEIGHT.semibold }}
              >
                Turn them on
              </Box>
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', p: 0.5, minWidth: 0 }}>
          {available.length === 0 ? (
            <Typography sx={{ ...subSx, p: 1 }}>
              {pool.length === 0
                ? 'No couriers are switched on. Turn some on in Couriers first.'
                : 'Everything matching is already in the priority list.'}
            </Typography>
          ) : (
            available.map((c) => {
              const isChecked = checked.includes(c.key);
              return (
                <Box
                  key={c.key}
                  sx={rowSx(isChecked)}
                  onClick={() =>
                    !readOnly &&
                    setChecked((list) =>
                      list.includes(c.key) ? list.filter((k) => k !== c.key) : [...list, c.key]
                    )
                  }
                >
                  <Checkbox size='small' checked={isChecked} disabled={readOnly} sx={{ p: 0.2, flexShrink: 0 }} />
                  <Tooltip title={`${c.name} · ${c.detail}`}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={nameSx}>{c.name}</Typography>
                      <Typography sx={codeSx}>{c.detail}</Typography>
                    </Box>
                  </Tooltip>
                  <OwnerChip owner={c.owner} />
                </Box>
              );
            })
          )}
        </Box>

        {!readOnly && (
          <Box sx={{ px: 1, py: 0.75, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: GAP.sm, flexShrink: 0 }}>
            <Button
              size='small'
              startIcon={<AddOutlinedIcon sx={{ fontSize: 15 }} />}
              disabled={checked.length === 0}
              onClick={() => addKeys(checked)}
              sx={{ ...secondaryBtnSx, borderColor: PRIMARY_BLUE, color: PRIMARY_BLUE }}
            >
              Add{checked.length ? ` ${checked.length}` : ''}
            </Button>
            <Button size='small' sx={secondaryBtnSx} disabled={available.length === 0} onClick={() => addKeys(available.map((c) => c.key))}>
              Add all shown
            </Button>
          </Box>
        )}
      </Box>

      {/* Right — order */}
      <Box sx={paneSx}>
        <Box sx={paneHeadSx}>
          <Typography sx={statLabelSx}>Priority</Typography>
          <Typography sx={subSx}>
            {order.length ? `${order.length} selected` : 'none selected'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {!readOnly && order.length > 0 && (
            <Button size='small' sx={secondaryBtnSx} onClick={() => onChange([])}>
              Clear
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 1, minWidth: 0 }}>
          {order.length === 0 ? (
            <Typography sx={subSx}>
              Pick couriers on the left. Allocation tries them top to bottom.
            </Typography>
          ) : (
            <PriorityList
              pool={pool.filter((c) => order.includes(c.key))}
              order={order}
              onChange={readOnly ? () => {} : onChange}
              onRemove={readOnly ? undefined : (key) => onChange(order.filter((k) => k !== key))}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
